import { useCallback, useEffect, useRef, useState } from 'react';

interface UseToastResult {
	message: string | null;
	isVisible: boolean;
	showToast: (message: string) => void;
	hideToast: () => void;
}

export const useToast = (): UseToastResult => {
	const [message, setMessage] = useState<string | null>(null);
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const duration = 2500;

	const clearExistingTimeout = useCallback(() => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
	}, []);

	const hideToast = useCallback(() => {
		clearExistingTimeout();
		setMessage(null);
	}, [clearExistingTimeout]);

	const showToast = useCallback(
		(newMessage: string) => {
			if (!newMessage) return;

			clearExistingTimeout();
			setMessage(newMessage);

			timeoutRef.current = setTimeout(() => {
				setMessage((current) => {
					// Only clear if the message hasn't changed since the timeout started
					if (current === newMessage) {
						return null;
					}
					return current;
				});
				timeoutRef.current = null;
			}, duration);
		},
		[clearExistingTimeout]
	);

	useEffect(() => () => clearExistingTimeout(), [clearExistingTimeout]);

	return {
		message,
		isVisible: Boolean(message),
		showToast,
		hideToast,
	};
};

export default useToast;
